import {
  HttpApiEndpoint,
  HttpApiGroup,
  HttpApiSchema,
  OpenApi,
} from 'effect/http-api';
import { Effect, Schema } from 'effect';
import { PublicDocPromotion } from './public-doc-promotion';
import { NotFoundError, InternalServerError } from './errors';

const PublicDocSummary = Schema.Struct({
  publicId: Schema.String,
  slug: Schema.String,
  title: Schema.NullOr(Schema.String),
  description: Schema.NullOr(Schema.String),
  language: Schema.NullOr(Schema.String),
  category: Schema.NullOr(Schema.String),
  coverImageUrl: Schema.NullOr(Schema.String),
  publishingAuthor: Schema.NullOr(Schema.String),
  publishingYear: Schema.NullOr(Schema.Int),
  createdAtMs: Schema.Int,
});

const PublicDocCategoryFacet = Schema.Struct({
  category: Schema.String,
});

export const PUBLIC_DOCS_DEFAULT_LIMIT = 50;
export const PUBLIC_DOCS_MAX_LIMIT = 100;

const ListPublicDocsResponse = Schema.Struct({
  items: Schema.Array(PublicDocSummary),
  cursor: Schema.NullOr(Schema.String),
  page: Schema.Int.pipe(Schema.withDecodingDefaultType(Effect.succeed(1))),
  limit: Schema.Int.pipe(
    Schema.withDecodingDefaultType(Effect.succeed(PUBLIC_DOCS_DEFAULT_LIMIT)),
  ),
  totalItems: Schema.Int.pipe(
    Schema.withDecodingDefaultType(Effect.succeed(0)),
  ),
  totalPages: Schema.Int.pipe(
    Schema.withDecodingDefaultType(Effect.succeed(1)),
  ),
  hasPreviousPage: Schema.Boolean.pipe(
    Schema.withDecodingDefaultType(Effect.succeed(false)),
  ),
  hasNextPage: Schema.Boolean.pipe(
    Schema.withDecodingDefaultType(Effect.succeed(false)),
  ),
  categoryFacets: Schema.Array(PublicDocCategoryFacet).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => [])),
  ),
});

const ListPublicDocsUrlParams = Schema.Struct({
  cursor: Schema.optional(Schema.String).annotate({
    description: 'Pagination cursor for the next page',
  }),
  page: Schema.FiniteFromString.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThanOrEqualTo(1)),
    Schema.withDecodingDefaultType(Effect.succeed(1)),
  ).annotate({
    description: 'Page number for traditional pagination',
  }),
  limit: Schema.FiniteFromString.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThanOrEqualTo(1)),
    Schema.check(Schema.isLessThanOrEqualTo(PUBLIC_DOCS_MAX_LIMIT)),
    Schema.withDecodingDefaultType(Effect.succeed(PUBLIC_DOCS_DEFAULT_LIMIT)),
  ).annotate({
    description: `Number of documents to return (1-${PUBLIC_DOCS_MAX_LIMIT}, default ${PUBLIC_DOCS_DEFAULT_LIMIT})`,
  }),
  category: Schema.optional(Schema.String).annotate({
    description: 'Filter by category',
  }),
  search: Schema.optional(Schema.String).annotate({
    description: 'Search documents by title',
  }),
});

const listPublicDocsEndpoint = HttpApiEndpoint.get('ListPublicDocs', '/', {
  query: ListPublicDocsUrlParams,
  success: ListPublicDocsResponse.pipe(HttpApiSchema.status(200)),
  error: [NotFoundError, InternalServerError],
})
  .annotate(OpenApi.Summary, 'List Public Docs')
  .annotate(
    OpenApi.Description,
    'List publicly available documents with traditional page pagination. Cursor pagination remains supported for compatibility.',
  );

const PublicDocDetail = Schema.Struct({
  publicId: Schema.String,
  slug: Schema.String,
  title: Schema.NullOr(Schema.String),
  description: Schema.NullOr(Schema.String),
  summary: Schema.NullOr(Schema.String),
  keyPoints: Schema.NullOr(Schema.String),
  category: Schema.NullOr(Schema.String),
  coverImageUrl: Schema.NullOr(Schema.String),
  publishingAuthor: Schema.NullOr(Schema.String),
  publishingYear: Schema.NullOr(Schema.Int),
});

const getPublicDocEndpoint = HttpApiEndpoint.get('GetPublicDoc', '/:publicId', {
  params: Schema.Struct({ publicId: Schema.String }),
  success: PublicDocDetail.pipe(HttpApiSchema.status(200)),
  error: [NotFoundError, InternalServerError],
})
  .annotate(OpenApi.Summary, 'Get Public Doc')
  .annotate(
    OpenApi.Description,
    'Get a single public document by its public ID',
  );

const DocMetadataResponse = Schema.Struct({
  audioId: Schema.String,
  doc: Schema.NullOr(PublicDocPromotion),
});

const getDocMetadataEndpoint = HttpApiEndpoint.get(
  'GetDocMetadata',
  '/metadata/:slug',
  {
    params: Schema.Struct({ slug: Schema.String }),
    success: DocMetadataResponse.pipe(HttpApiSchema.status(200)),
    error: InternalServerError,
  },
)
  .annotate(OpenApi.Summary, 'Get Doc Metadata')
  .annotate(
    OpenApi.Description,
    'Look up curated public document metadata from the static metadata catalog by slug',
  );

export const publicDocGroup = HttpApiGroup.make('PublicDoc')
  .add(listPublicDocsEndpoint)
  .add(getPublicDocEndpoint)
  .add(getDocMetadataEndpoint)
  .prefix('/v2/public-docs');
